import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
    History, 
    Play, 
    MessageSquare, 
    Mic, 
    Clock,
    ChevronRight,
    RefreshCw
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export const SessionHistory = ({ onResumeSession, onViewSession }) => {
    const { api } = useAuth();
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSessions();
    }, []);

    const fetchSessions = async () => {
        try {
            const response = await api.get('/training/sessions');
            setSessions(response.data.slice(0, 10));
        } catch (error) {
            console.error('Failed to fetch sessions:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return <Badge variant="success" className="bg-emerald-500/20 text-emerald-500">Completed</Badge>;
            case 'active':
                return <Badge variant="default" className="bg-primary/20 text-primary">Active</Badge>;
            case 'paused':
                return <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-600">Paused</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    if (loading) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                    <History className="h-5 w-5" />
                    Recent Sessions
                </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
                <ScrollArea className="h-[300px]">
                    {sessions.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                            <MessageSquare className="h-8 w-8 text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground">No training sessions yet</p>
                            <p className="text-xs text-muted-foreground">Start a new session to see your history</p>
                        </div>
                    ) : (
                        <div className="divide-y">
                            {sessions.map((session) => (
                                <div 
                                    key={session.id}
                                    className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                                    onClick={() => onViewSession?.(session)}
                                >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <div className="p-2 rounded-lg bg-primary/10">
                                            <MessageSquare className="h-4 w-4 text-primary" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm font-medium truncate">
                                                    Session
                                                </p>
                                                {getStatusBadge(session.status)}
                                            </div>
                                            <div className="flex items-center gap-3 mt-1">
                                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                    <Clock className="h-3 w-3" />
                                                    {formatDistanceToNow(new Date(session.started_at), { addSuffix: true })}
                                                </span>
                                                <span className="text-xs text-muted-foreground">
                                                    {session.messages?.length || 0} messages
                                                </span>
                                                <span className="text-xs text-muted-foreground">
                                                    {Math.round(session.progress)}% complete
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {(session.status === 'active' || session.status === 'paused') && (
                                            <Button 
                                                size="sm" 
                                                variant="ghost"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onResumeSession?.(session);
                                                }}
                                            >
                                                <Play className="h-4 w-4 mr-1" />
                                                Resume
                                            </Button>
                                        )}
                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </ScrollArea>
            </CardContent>
        </Card>
    );
};
